import PageHeader from './PageHeader';
import Footer from 'src/components/Footer';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Helmet } from 'react-helmet-async';

import {
  Grid,
  LinearProgress,
  TextField,
  Box,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert
} from '@mui/material';

import OrderSalesSummary from './OrderSalesSummary';
// import BatchTransaction from './BatchTransaction';
import { useEffect, useState } from 'react';
import {
  BatchCloseOutSummaryResponse,
  OrdersReportDailySummary
} from 'src/services/apiService/response-models/report';
import dayjs from 'dayjs';
import { getDeviceList, getEmployeeList } from 'src/services/apiService';
import { ClientApp, Employee } from 'src/services/apiService/response-models';
import { getDailySalesSummaryReport } from 'src/services/apiService/reports';
import SalesByOrderType from './SalesByOrderType';
import SalesByPaymentType from './SalesByPaymentType';
import PriceEnhancersSummary from './PriceEnhancersSummary';

function getPeriodRange(period: string) {
  const today = dayjs();
  switch (period) {
    case 'today':
      return {
        from: today.format('MM-DD-YYYY'),
        to: today.format('MM-DD-YYYY')
      };
    case 'yesterday':
      return {
        from: today.subtract(1, 'day').format('MM-DD-YYYY'),
        to: today.subtract(1, 'day').format('MM-DD-YYYY')
      };
    case 'last_week':
      return {
        from: today.subtract(7, 'day').format('MM-DD-YYYY'),
        to: today.format('MM-DD-YYYY')
      };
    case 'last_month':
      return {
        from: today.subtract(1, 'month').startOf('month').format('MM-DD-YYYY'),
        to: today.subtract(1, 'month').endOf('month').format('MM-DD-YYYY')
      };
    case 'last_year':
      return {
        from: today.subtract(1, 'year').startOf('year').format('MM-DD-YYYY'),
        to: today.subtract(1, 'year').endOf('year').format('MM-DD-YYYY')
      };
    default:
      return null;
  }
}

function DailySalesReports() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dailySalesSummary, setDailySalesSummary] =
    useState<OrdersReportDailySummary | null>(null);

  const [fromDate, setFromDate] = useState(
    dayjs().subtract(7, 'day').format('MM-DD-YYYY')
  );
  const [toDate, setToDate] = useState(dayjs().format('MM-DD-YYYY'));
  const [registers, setRegisters] = useState<ClientApp[]>([]);
  const [selectedRegister, setSelectedRegister] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  useEffect(() => {
    // Fetch register/device list on mount
    getDeviceList().then((res) => {
      setRegisters(res.data || []);
    });
    getEmployeeList().then((res) => {
      setEmployees(res.data || []);
    });
  }, []);

  const getItems = async (
    from = fromDate,
    to = toDate,
    appRegisterId = selectedRegister,
    employeeId = selectedEmployee
  ) => {
    try {
      setError('');
      const response = await getDailySalesSummaryReport(
        from,
        to,
        appRegisterId || undefined,
        employeeId || undefined
      );
      setDailySalesSummary(response.data);
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    } catch (err: any) {
      setDailySalesSummary(null);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load items'
      );
      setIsLoading(false);
    }
  };
  // Call the function to fetch items
  useEffect(() => {
    setIsLoading(true);
    getItems();
  }, []);

  const handleFilter = () => {
    setIsLoading(true);
    getItems(fromDate, toDate, selectedRegister, selectedEmployee);
  };

  const handlePeriodChange = (periodValue: string) => {
    const range = getPeriodRange(periodValue);
    if (range) {
      setFromDate(range.from);
      setToDate(range.to);
      setIsLoading(true);
      getItems(range.from, range.to);
    }
  };

  return (
    <>
      <Helmet>
        <title>Batch Close Out | Reports Dashboard</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader onPeriodChange={handlePeriodChange} />
      </PageTitleWrapper>
      <Box sx={{ display: 'flex', gap: 2, px: 4, mb: 2 }}>
        {/* Register Filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="register-select-label">Register</InputLabel>
          <Select
            labelId="register-select-label"
            value={selectedRegister}
            label="Register"
            onChange={(e) => setSelectedRegister(e.target.value)}
          >
            <MenuItem value="">All Registers</MenuItem>
            {registers.map((reg: any) => (
              <MenuItem key={reg.id} value={reg.clientApiKey}>
                {reg.appName || reg.deviceId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range Filters */}
        <TextField
          label="From"
          type="date"
          size="small"
          value={dayjs(fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')}
          onChange={(e) =>
            setFromDate(dayjs(e.target.value).format('MM-DD-YYYY'))
          }
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={dayjs(toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')}
          onChange={(e) =>
            setToDate(dayjs(e.target.value).format('MM-DD-YYYY'))
          }
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={handleFilter}>
          Apply
        </Button>
      </Box>
      {error && (
        <Box sx={{ px: 4, mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {isLoading && <LinearProgress sx={{ my: 2 }} />}
      <Grid
        sx={{
          px: 4
        }}
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={4}
      >
        <Grid item xs={12}>
          <OrderSalesSummary summary={dailySalesSummary} />
        </Grid>
          <Grid md={6} item xs={12}>
            <SalesByOrderType
              salesByOrderType={
                dailySalesSummary?.grandOrderSummary?.salesByOrderType
              }
            />
          </Grid>
          <Grid md={6} item xs={12}>
            <PriceEnhancersSummary
              priceEnhancers={
                dailySalesSummary?.grandOrderSummary?.priceEnhancers
              }
            />
          </Grid>
        <Grid item  xs={12}>
          <SalesByPaymentType
            salesByPaymentType={
              dailySalesSummary?.grandOrderSummary?.salesByPaymentType
            }
          />
        </Grid>
      </Grid>
      <Footer />
    </>
  );
}

export default DailySalesReports;
